import React, { useEffect, useRef, useState } from 'react';
import { ActionIcon, Button, FileInput, Modal, PasswordInput, Select, TextInput, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCircleCheck, faPen } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button as SharedButton } from '../../../../components/Button';
import type { EmployeeView } from '../../../../services/employees/employees.service';
import settingsClasses from './shared/settings-shared.module.scss';
import navyModal from '../../../../components/Modal/navy-modal.module.scss';
import classes from './pos-employees-settings-content.module.scss';

type EmployeeFormState = { fullName: string; pin: string; role: 'manager' | 'employee'; baseHourlySalary: string };

type PosEmployeesSettingsContentProps = {
  form: EmployeeFormState;
  photoFile: File | null;
  editingEmployeeId: number | null;
  settingsEmployees: EmployeeView[];
  isLoadingSettings: boolean;
  isSavingEmployee: boolean;
  attendanceUiEnabled: boolean;
  onChangeForm: (key: keyof EmployeeFormState, value: string) => void;
  onPhotoFileChange: (file: File | null) => void;
  onRefreshEmployees: () => void;
  onResetForm: () => void;
  onSaveEmployee: (event: React.FormEvent<HTMLFormElement>) => void;
  onEditEmployee: (employee: EmployeeView) => void;
  onDeactivateEmployee: (employee: EmployeeView) => void;
  onActivateEmployee: (employee: EmployeeView) => void;
};

const PosEmployeesSettingsContentComponent: React.FC<PosEmployeesSettingsContentProps> = ({
  form, photoFile, editingEmployeeId, settingsEmployees, isLoadingSettings, isSavingEmployee, attendanceUiEnabled,
  onChangeForm, onPhotoFileChange, onRefreshEmployees, onResetForm, onSaveEmployee, onEditEmployee,
  onDeactivateEmployee, onActivateEmployee,
}) => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const isFormModalOpen = isCreateModalOpen || editingEmployeeId !== null;
  const isEditMode = editingEmployeeId !== null;
  const isEmployeeEditorTabletFullscreen = useMediaQuery('(max-width: 1200px)', false);
  const wasSavingEmployeeRef = useRef(false);

  useEffect(() => {
    if (wasSavingEmployeeRef.current && !isSavingEmployee && !isEditMode && isCreateModalOpen) setCreateModalOpen(false);
    wasSavingEmployeeRef.current = isSavingEmployee;
  }, [isCreateModalOpen, isEditMode, isSavingEmployee]);

  const closeFormModal = () => {
    if (isSavingEmployee) return;
    setCreateModalOpen(false);
    onResetForm();
  };

  return (
    <section className={settingsClasses.section}>
      <div className={settingsClasses.toolbar}>
        <SharedButton type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="employees-settings-create-open">צור עובד</SharedButton>
        <Button type="button" variant="default" radius="md" data-testid="employees-settings-refresh" onClick={onRefreshEmployees} disabled={isLoadingSettings}>{isLoadingSettings ? 'מרענן...' : 'רענן'}</Button>
      </div>
      <div className={classes.grid} data-testid="employees-settings-cards">
        {settingsEmployees.map((employee) => (
          <article key={employee.id} className={classes.card} data-testid={`employees-settings-card-${employee.id}`}>
            <div className={classes.cardHeader}>
              {employee.photoUrl ? <img src={employee.photoUrl} alt={`${employee.fullName} profile`} className={classes.photo} /> : <span className={classes.initial}>{employee.fullName.trim().charAt(0).toUpperCase() || '?'}</span>}
              <div className={classes.identity} data-testid={`employees-settings-details-${employee.id}`}>
                <strong>{employee.fullName}</strong>
                {attendanceUiEnabled && <span className={classes.salary}>שכר שעתי <strong>{employee.baseHourlySalary == null ? '—' : `${employee.baseHourlySalary}`}</strong></span>}
                <span>{employee.role === 'manager' ? 'מנהל' : 'עובד'}</span>
              </div>
              <div className={classes.statusActions}>
                <span className={employee.isActive ? settingsClasses.statusActive : settingsClasses.statusInactive}>{employee.isActive ? 'פעיל' : 'לא פעיל'}</span>
                <div className={classes.actions}>
                  <Tooltip label="ערוך" withArrow><ActionIcon aria-label="ערוך" data-testid={`employees-settings-edit-${employee.id}`} variant="subtle" color="gray" radius="md" size="lg" onClick={() => onEditEmployee(employee)}><FontAwesomeIcon icon={faPen as IconProp} /></ActionIcon></Tooltip>
                  {employee.isActive ? (
                    <Tooltip label="השבת" withArrow><ActionIcon aria-label="השבת" data-testid={`employees-settings-toggle-${employee.id}`} variant="subtle" color="red" radius="md" size="lg" onClick={() => onDeactivateEmployee(employee)}><FontAwesomeIcon icon={faBan as IconProp} /></ActionIcon></Tooltip>
                  ) : (
                    <Tooltip label="הפעל" withArrow><ActionIcon aria-label="הפעל" data-testid={`employees-settings-toggle-${employee.id}`} variant="subtle" color="green" radius="md" size="lg" onClick={() => onActivateEmployee(employee)}><FontAwesomeIcon icon={faCircleCheck as IconProp} /></ActionIcon></Tooltip>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
        {settingsEmployees.length === 0 && !isLoadingSettings && <p className={classes.empty}>אין עובדים לטננט זה עדיין.</p>}
      </div>
      <Modal opened={isFormModalOpen} onClose={closeFormModal} title={isEditMode ? 'עריכת עובד' : 'עובד חדש'} size={isEmployeeEditorTabletFullscreen ? '100vw' : 'lg'} centered={!isEmployeeEditorTabletFullscreen} fullScreen={isEmployeeEditorTabletFullscreen} radius="md" classNames={{ root: `${navyModal.root} ${classes.employeeEditorModal} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorTabletFullscreenRoot : ''}` }} closeButtonProps={{ disabled: isSavingEmployee, 'data-testid': 'employees-settings-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']} closeOnClickOutside={!isSavingEmployee} closeOnEscape={!isSavingEmployee}>
        <form onSubmit={onSaveEmployee} className={`${settingsClasses.section} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorForm : ''}`} onContextMenu={(event) => event.preventDefault()}>
          <div className={classes.employeeEditorFields}>
            <TextInput data-testid="employees-settings-name" label="שם מלא" placeholder="שם מלא" value={form.fullName} onChange={(event) => onChangeForm('fullName', event.currentTarget.value)} minLength={2} maxLength={120} required radius="md" />
            <PasswordInput data-testid="employees-settings-pin" label={isEditMode ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'} placeholder={isEditMode ? 'קוד עובד חדש (אופציונלי)' : 'קוד עובד'} value={form.pin} onChange={(event) => onChangeForm('pin', event.currentTarget.value)} minLength={isEditMode ? undefined : 4} maxLength={10} required={!isEditMode} radius="md" />
            <Select data-testid="employees-settings-role" label="תפקיד" data={[{ value: 'employee', label: 'עובד' }, { value: 'manager', label: 'מנהל' }]} value={form.role} onChange={(value) => onChangeForm('role', (value ?? 'employee') as 'manager' | 'employee')} radius="md" />
            {attendanceUiEnabled && <TextInput data-testid="employees-settings-hourly-salary" label="שכר שעתי" placeholder="לדוגמה: 45" value={form.baseHourlySalary} onChange={(event) => onChangeForm('baseHourlySalary', event.currentTarget.value)} inputMode="decimal" radius="md" />}
          </div>
          <div className={settingsClasses.fileField}>
            <label className={settingsClasses.fileFieldLabel}>תמונת עובד</label>
            <FileInput data-testid="employees-settings-photo" accept="image/jpeg,image/webp" onChange={onPhotoFileChange} placeholder="בחר קובץ" aria-label="Employee Photo" radius="md" clearable />
            {photoFile && <span className={settingsClasses.lead}>תמונה נבחרה: {photoFile.name}</span>}
          </div>
          <div className={`${settingsClasses.formActions} ${isEmployeeEditorTabletFullscreen ? classes.employeeEditorFooter : ''}`}>
            <Button type="button" variant="default" radius="md" onClick={closeFormModal} disabled={isSavingEmployee} data-testid="employees-settings-cancel-edit">ביטול</Button>
            <SharedButton type="submit" variant="save" size="sm" disabled={isSavingEmployee} dataTestId="employees-settings-submit">{isSavingEmployee ? 'שומר...' : isEditMode ? 'שמור עובד' : 'צור עובד'}</SharedButton>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export const PosEmployeesSettingsContent = React.memo(PosEmployeesSettingsContentComponent);
